import { cloneElement, useState } from 'react'
import type { DragEvent, HTMLAttributes, ReactElement, ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline'
import { useGetAuthenticatedUser } from '../../../Flux/Selector/index.ts'
import { canUseOrderWizard } from '../../../Model/User/featureGating.ts'
import { handOffToWizard } from '../../../Services/OrderWizard/handoff.ts'
import type { WizardEntryVia, WizardHandoff } from '../../../Services/OrderWizard/handoff.ts'
import { DROPZONE_ACCEPT } from '../../../Services/OrderWizard/wizardFiles.ts'
import { carriesFiles } from './useFileDrag.ts'
import { useFilePicker } from './useFilePicker.tsx'

export const ORDER_WIZARD_PATH = '/orderWizard'

/** Dropped or picked files go to the wizard page, which drops them into a new draft. */
const HandoffDropArea = ({
  source,
  testId,
  className,
  children
}: {
  source: WizardHandoff['source']
  testId: string
  className: string
  /** The prompt; `browse` is the button that opens the picker. */
  children: (browse: ReactNode) => ReactNode
}) => {
  const navigate = useNavigate()
  const [over, setOver] = useState(false)
  const handOff = (via: WizardEntryVia, files: File[]) => {
    handOffToWizard({ source, via, files })
    navigate(ORDER_WIZARD_PATH)
  }
  const picker = useFilePicker({ accept: DROPZONE_ACCEPT, onFiles: (files) => handOff('pick', files), testId: `${testId}-input` })
  const browse = (
    <button
      type="button"
      className="link link-hover font-medium text-slate-800"
      data-testid={`${testId}-browse`}
      onClick={(event) => {
        event.stopPropagation()
        picker.open()
      }}
    >
      browse
    </button>
  )
  return (
    <div
      data-testid={testId}
      className={`cursor-pointer border border-dashed transition-colors ${over ? 'border-primary bg-primary/5' : 'border-slate-300 bg-white hover:border-slate-400'} ${className}`}
      onClick={picker.open}
      onDragOver={(event) => {
        if (carriesFiles(event.nativeEvent)) {
          event.preventDefault()
          event.dataTransfer.dropEffect = 'copy'
          setOver(true)
        }
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setOver(false)
        }
      }}
      onDrop={(event) => {
        if (!carriesFiles(event.nativeEvent)) {
          return
        }
        event.preventDefault()
        event.stopPropagation()
        setOver(false)
        const files = Array.from(event.dataTransfer.files ?? [])
        if (files.length > 0) {
          handOff('drop', files)
        }
      }}
    >
      {children(browse)}
      {picker.input}
    </div>
  )
}

/** Above the classic "Using FASTQ raw data" form. */
export const ClassicCreateEntry = () => {
  const user = useGetAuthenticatedUser()
  if (!canUseOrderWizard(user)) {
    return null
  }
  return (
    <div className="mx-auto mb-4 w-full max-w-2xl" data-testid="wizard-entry-classic">
      <HandoffDropArea
        source="classic-create"
        testId="wizard-entry-classic-drop"
        className="flex items-center gap-3 rounded-2xl px-4 py-3 text-sm text-slate-700"
      >
        {(browse) => (
          <>
            <ArrowUpTrayIcon className="h-5 w-5 shrink-0 text-primary" aria-hidden />
            <span>
              Have your FASTQ files ready? Drop them here — we set up the order for you{' '}
              <span className="text-xs text-slate-500">(preview)</span>
              <span className="block text-xs text-slate-500">Or {browse} for them.</span>
            </span>
          </>
        )}
      </HandoffDropArea>
      <div className="mt-1 text-right text-xs">
        <Link
          to={ORDER_WIZARD_PATH}
          className="link link-hover text-slate-500"
          data-testid="wizard-entry-classic-link"
          onClick={() => handOffToWizard({ source: 'classic-create', via: 'link', files: [] })}
        >
          Open the new order wizard
        </Link>
      </div>
    </div>
  )
}

/** Hands files to the wizard from the dashboard, then opens it. */
const useDashboardHandoff = () => {
  const navigate = useNavigate()
  return (via: WizardEntryVia, files: File[]) => {
    handOffToWizard({ source: 'dashboard', via, files })
    navigate(ORDER_WIZARD_PATH)
  }
}

/** An option card the drop target takes over: its own element, so it stays the grid's item. */
type DropOption = ReactElement<HTMLAttributes<HTMLElement> & Record<`data-${string}`, unknown>>

/** The tile's FASTQ option as a drop target: file drags only, a click still follows the option's own link. */
const DashboardDropTarget = ({ option }: { option: DropOption }) => {
  const handOff = useDashboardHandoff()
  const [over, setOver] = useState(false)
  return cloneElement(option, {
    className: over ? `${option.props.className ?? ''} relative ring-2 ring-primary` : option.props.className,
    'data-testid': 'wizard-entry-dashboard',
    'data-over': over || undefined,
    onDragOver: (event: DragEvent<HTMLElement>) => {
      if (carriesFiles(event.nativeEvent)) {
        event.preventDefault()
        event.dataTransfer.dropEffect = 'copy'
        setOver(true)
      }
    },
    onDragLeave: (event: DragEvent<HTMLElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        setOver(false)
      }
    },
    onDrop: (event: DragEvent<HTMLElement>) => {
      if (!carriesFiles(event.nativeEvent)) {
        return
      }
      event.preventDefault()
      event.stopPropagation()
      setOver(false)
      const files = Array.from(event.dataTransfer.files ?? [])
      if (files.length > 0) {
        handOff('drop', files)
      }
    },
    children: (
      <>
        {option.props.children}
        {over && (
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-white/90 text-sm font-medium text-primary"
            data-testid="wizard-entry-dashboard-over"
          >
            Drop to start a new order (preview)
          </div>
        )}
      </>
    )
  })
}

const DashboardDropLine = () => (
  <p className="mt-3 text-sm text-slate-500" data-testid="wizard-entry-dashboard-line">
    Or drop FASTQ files on the tile above — we set up the order for you (preview)
  </p>
)

/**
 * The dashboard's "Create order" options with the wizard in them: `children` lays the options out and
 * passes the FASTQ option card through `dropTarget`, which makes that card the drop target.
 */
export const DashboardEntry = ({ children }: { children: (dropTarget: (option: DropOption) => ReactNode) => ReactNode }) => {
  const user = useGetAuthenticatedUser()
  if (!canUseOrderWizard(user)) {
    return <>{children((option) => option)}</>
  }
  return (
    <>
      {children((option) => (
        <DashboardDropTarget option={option} />
      ))}
      <DashboardDropLine />
    </>
  )
}
